import React from 'react';
import { Button } from './Button';
import { ModalBase } from './ModalBase';

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  onOpenContactModal: (subject?: string) => void;
}

interface PlanCardProps {
  title: string;
  price: string;
  priceDetails: string;
  features: string[];
  isRecommended?: boolean;
  onSelect: () => void;
  buttonText: string;
}

const CheckIcon: React.FC = () => (
  <svg className="w-5 h-5 text-green-500 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
  </svg>
);


const PlanCard: React.FC<PlanCardProps> = ({ title, price, priceDetails, features, isRecommended = false, onSelect, buttonText }) => (
  <div className={`flex flex-col rounded-lg shadow-lg overflow-hidden h-full ${isRecommended ? 'border-2 border-blue-500' : 'border'}`}>
     {isRecommended && (
      <div className="bg-blue-500 text-white text-center py-1 font-semibold text-sm">
        RECOMENDADO
      </div>
    )}
    <div className="px-6 py-8 bg-white sm:p-10 sm:pb-6 flex-grow">
      <h3 className="text-lg font-medium text-gray-900">{title}</h3>
      <div className="mt-4 flex items-baseline text-4xl font-extrabold text-gray-900">
        {price}
        <span className="ml-1 text-xl font-medium text-gray-500">{priceDetails}</span>
      </div>
      <ul className="mt-6 space-y-4">
        {features.map((feature, index) => (
          <li key={index} className="flex items-start">
            <CheckIcon />
            <span className="text-base text-gray-600">{feature}</span>
          </li>
        ))}
      </ul>
    </div>
    <div className="flex-shrink-0 px-6 pt-4 pb-8 bg-white sm:p-10 sm:pt-6">
      <Button onClick={onSelect} className="w-full" variant={isRecommended ? 'primary' : 'secondary'}>
        {buttonText}
      </Button>
    </div>
  </div>
);


export const PricesModal: React.FC<ModalProps> = ({ isOpen, onClose, onOpenContactModal }) => {
  const handleSelectPlan = (planTitle: string) => {
    onClose();
    onOpenContactModal(`Consulta sobre el plan: ${planTitle}`);
  };
  
  return (
    <ModalBase 
      isOpen={isOpen} 
      onClose={onClose} 
      title="Planes y Precios"
      containerClassName="max-w-4xl"
      mainClassName="bg-gray-50"
    >
      <div className="text-center mb-12">
        <h3 className="text-3xl font-bold text-gray-800">Planes para cada necesidad</h3>
        <p className="mt-4 text-lg text-gray-600 max-w-2xl mx-auto">
            Elige el plan que mejor se adapta a ti. Transparencia total, sin costes ocultos.
        </p>
      </div>

      <div className="grid md:grid-cols-3 gap-8">
        <PlanCard
          title="Defensa Esencial"
          price="49€"
          priceDetails="/ multa"
          features={[
            "Análisis de viabilidad del recurso",
            "Redacción y presentación de alegaciones",
            "Seguimiento online del expediente"
          ]}
          onSelect={() => handleSelectPlan("Defensa Esencial")}
          buttonText="Empezar"
        />
        <PlanCard
          title="Defensa Avanzada"
          price="89€"
          priceDetails="/ multa"
          features={[
            "Todo lo del plan Esencial",
            "Gestión de identificación de conductor",
            "Presentación de recurso de alzada",
            "Asesoramiento sobre pronto pago"
          ]}
          onSelect={() => handleSelectPlan("Defensa Avanzada")}
          buttonText="Elegir Plan"
          isRecommended
        />
        <PlanCard
          title="Protección Anual"
          price="199€"
          priceDetails="/ año"
          features={[
            "Cobertura para todas tus multas (hasta 5)",
            "Asesoramiento legal continuo",
            "Gestión y control de puntos del carnet",
            "Incluye defensa avanzada en cada caso"
          ]}
          onSelect={() => handleSelectPlan("Protección Anual")}
          buttonText="Contratar Anual"
        />
      </div>
      <div className="mt-12 text-center text-sm text-gray-500">
        <p>¿Necesitas un informe pericial o representación judicial? Estos servicios se presupuestan de forma individualizada. <button onClick={() => onOpenContactModal('Solicitud de presupuesto a medida')} className="font-medium text-blue-600 hover:underline">Contacta con nosotros</button> para un presupuesto a medida.</p>
        <p className="mt-1">Todos los precios incluyen IVA donde sea aplicable.</p>
      </div>
    </ModalBase>
  );
};
